import { useQuery } from '@apollo/react-hooks';
import { Box } from 'localmed-core';
import React, { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { UniversalContext } from '../../../../_universal';
import { withCustomContexts } from '../../../../shared/components/Contexts';
import {
  DiGenericLoadingPage,
  DiLayout,
  DiNotFoundPage,
  LmHeader,
} from '../../../../shared/components/DiLayout';
import {
  DiPatientScreener,
  PatientScreenerFailure,
  SCREENER_STATUSES,
} from '../../../../shared/components/PatientScreener';
import {
  SchedulingSessionStateContext,
  createSchedulingSessionStateLandingPage,
} from '../../../../shared/components/SchedulingSessionState';
import { ScreenerStatus } from '../../../../shared/components/SchedulingSessionState/constants';
import { LM_CLIENT_CONTEXT } from '../../../../shared/constants';
import { trackAffiliateEvent } from '../../../../shared/utils/affiliateTracking';
import isForbidden from '../../../../shared/utils/isForbidden';
import { IRouteProps, ITrackerData, IWidgetSettings } from '../../../../types';
import AnnouncementBanner from '../../../components/AnnouncementBanner';
import WidgetSessionOpenedMutation from '../../../components/WidgetSessionOpenedMutation';
import { SchedulingErrorPage, SchedulingNotFoundPage } from '../components';
import SchedulingPageBody from '../components/SchedulingPageBody';
import query from './NewOfficeSchedulingPageQuery.graphql';
import ScreenerSuccess from './ScreenerSuccess';

interface IProps extends IRouteProps {
  widgetSettings: IWidgetSettings;
  trackerData: ITrackerData;
  patientScreenerStatus: ScreenerStatus;
  prefillToken: string;
}

function NewOfficeSchedulingPage({
  history,
  match,
  trackerData,
  widgetSettings,
  prefillToken,
}: IProps) {
  const { id } = match.params;
  const { trackEvent } = useContext(UniversalContext);
  const trackedRef = useRef(false);
  const [hasPassedInitialScreening, setHasPassedInitialScreening] = useState(false);
  const { screenerStatus } = useContext(SchedulingSessionStateContext);

  const { data, loading, error, refetch } = useQuery(query, {
    variables: { id },
    context: LM_CLIENT_CONTEXT,
  });

  const { office } = data || {};

  const showScheduling =
    screenerStatus === SCREENER_STATUSES.PASSED || !office?.patientScreenerEnabled;
  const { announcementEnabled, announcement } = office || {};

  useEffect(() => {
    if (loading || trackedRef.current) return;

    if (office) {
      trackEvent({
        event: 'Viewed Office',
        properties: {
          officeId: office.id,
          officeDisplay: office.display,
          practiceId: office.practice.id,
          practiceDisplay: office.practice.display,
          reasonForVisitCount: office.reasonsForVisit.nodes.length,
          viewOpeningsBy: office.viewOpeningsBy && office.viewOpeningsBy[0],
          widget: true,
        },
      });
    } else {
      trackEvent({
        event: 'Office Widget Not Found',
        properties: { officeId: id },
      });
    }

    trackEvent({ event: 'Viewed Widget Version 2' });
    trackAffiliateEvent({
      aftId: widgetSettings.aftId,
      aftType: widgetSettings.aftType,
      aft: widgetSettings.aft,
      event: 'Viewed LocalMed Widget',
    });

    trackedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    if (screenerStatus === SCREENER_STATUSES.PASSED) {
      setHasPassedInitialScreening(true);
    }
  }, [screenerStatus]);

  if (loading) return <DiGenericLoadingPage />;

  if (isForbidden(error)) {
    return <DiNotFoundPage />;
  }

  if (error) {
    return <SchedulingErrorPage refetch={refetch} />;
  }

  if (!office) {
    return <SchedulingNotFoundPage />;
  }

  const trackSchedulingEvent = (
    event,
    component = 'OfficeSchedulingPage',
    additionalProperties = {}
  ) => {
    trackEvent({
      event,
      properties: {
        workflow: 'Office Scheduling',
        component,
        officeId: office.id,
        officeDisplay: office.display,
        practiceId: office.practice.id,
        practiceDisplay: office.practice.display,
        widget: true,
        ...additionalProperties,
      },
    });
  };

  return (
    <DiLayout
      header={<LmHeader office={office} practice={office.practice} />}
      footerText="Powered by LocalMed"
      fullWidth
    >
      <Helmet>
        <title>
          {`Schedule an Appointment | ${office.messagingName || office.practice.display}`}
        </title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <WidgetSessionOpenedMutation widgetId={trackerData?.widgetId} />
      <Fragment>
        <ScreenerSuccess />
        {showScheduling ? (
          <Fragment>
            {announcementEnabled && (
              <AnnouncementBanner
                announcement={announcement}
                backgroundColor="lightOrange"
                buttonColor="black"
                hideIcon
              />
            )}
            <SchedulingPageBody
              history={history}
              office={office}
              trackSchedulingEvent={trackSchedulingEvent}
              trackerData={trackerData}
              widgetSettings={widgetSettings}
              hasPassedInitialScreening={hasPassedInitialScreening}
              prefillToken={prefillToken}
            />
          </Fragment>
        ) : (
          <Box maxWidth="720px" m="0 auto">
            {screenerStatus === SCREENER_STATUSES.NOT_SCREENED && (
              <DiPatientScreener officeId={office?.id} officeDisplay={office?.display} widget />
            )}
            {screenerStatus === SCREENER_STATUSES.FAILED && (
              <PatientScreenerFailure phoneNumber={office?.phone} />
            )}
          </Box>
        )}
      </Fragment>
    </DiLayout>
  );
}

export default createSchedulingSessionStateLandingPage(withCustomContexts(NewOfficeSchedulingPage));
